/**
 * NexusCodeChat — Workspace de chat do NEXUS CODE.
 *
 * UX completo tipo Emergent:
 *  - Pipeline status bar: planning -> coding -> validating -> done|failed
 *  - Feed de mensagens: user / agent text / tool cards / approval gate real
 *  - Approval gate com diff real (approve/reject)
 *  - Files changed panel (tab Code)
 *  - Credits (balance, nao runs_left)
 *  - WebSocket real conectado ao backend
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ncConversations, ncExecute, ncApprovals, ncBilling,
  createNcSocket,
} from "./nexuscodeApi";

const T = {
  bg:      "#030407",
  surface: "#080910",
  surf2:   "#0d0f1c",
  border:  "#13152a",
  accent:  "#7c3aed",
  accentL: "#a78bfa",
  accentS: "rgba(124,58,237,0.12)",
  accentB: "rgba(124,58,237,0.25)",
  txt:     "#f1f5f9",
  muted:   "#64748b",
  dim:     "#1e2335",
  green:   "#34d399",
  yellow:  "#fbbf24",
  red:     "#f87171",
};

const PIPELINE_STAGES = [
  { id: "planning",   label: "Planeamento", icon: "🧠" },
  { id: "coding",     label: "Codificacao",  icon: "⚡" },
  { id: "validating", label: "Validacao",    icon: "🔍" },
  { id: "done",       label: "Concluido",    icon: "✅" },
];

function PipelineBar({ stage }) {
  if (!stage || stage === "idle") return null;
  const failed  = stage === "failed";
  const doneIdx = failed ? -1 : PIPELINE_STAGES.findIndex(s => s.id === stage);

  return (
    <div style={{
      display: "flex", alignItems: "center",
      background: T.surf2, borderBottom: `1px solid ${T.border}`,
      padding: "7px 20px", flexShrink: 0, overflowX: "auto", gap: 0,
    }}>
      {failed ? (
        <div style={{ fontSize: 11, color: T.red, display: "flex", alignItems: "center", gap: 6 }}>
          <span>✕</span> Execucao falhada
        </div>
      ) : PIPELINE_STAGES.map((s, i) => {
        const isActive   = i === doneIdx;
        const isComplete = i < doneIdx || stage === "done";
        const color = isComplete ? T.green : isActive ? T.accentL : T.muted;
        return (
          <React.Fragment key={s.id}>
            <div style={{
              display: "flex", alignItems: "center", gap: 5,
              fontSize: 11, fontWeight: isActive || isComplete ? 600 : 400,
              color, whiteSpace: "nowrap",
            }}>
              <span style={{ fontSize: 13 }}>
                {isComplete && stage !== s.id ? "✓" : s.icon}
              </span>
              {s.label}
            </div>
            {i < PIPELINE_STAGES.length - 1 && (
              <div style={{
                width: 24, height: 1, margin: "0 8px",
                background: i < doneIdx ? T.green : T.border, flexShrink: 0,
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function renderText(text) {
  if (!text) return null;
  const codeRe = /```(\w*)\n?([\s\S]*?)```/g;
  const parts = [];
  let last = 0, m;
  while ((m = codeRe.exec(text)) !== null) {
    if (m.index > last) parts.push({ t: "text", v: text.slice(last, m.index) });
    parts.push({ t: "code", lang: m[1], v: m[2] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ t: "text", v: text.slice(last) });

  return parts.map((p, i) => {
    if (p.t === "code") return (
      <pre key={i} style={{
        background: "#040509", border: `1px solid ${T.border}`, borderRadius: 10,
        padding: "12px 16px", overflowX: "auto", fontSize: 12,
        color: "#c4d0f0", margin: "8px 0", lineHeight: 1.6,
      }}>
        {p.lang && <span style={{ fontSize: 10, color: T.muted, display: "block", marginBottom: 4 }}>{p.lang}</span>}
        <code>{p.v}</code>
      </pre>
    );
    const html = p.v
      .replace(/`([^`]+)`/g, `<code style="background:#0d0f1c;border-radius:4px;padding:1px 5px;font-size:12px;color:#a78bfa">$1</code>`)
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .split("\n").join("<br/>");
    return <span key={i} dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

function DiffView({ diff }) {
  if (!diff) return null;
  return (
    <pre style={{
      background: "#040509", border: `1px solid ${T.border}`,
      borderRadius: 8, padding: "10px 14px", fontSize: 11.5,
      overflowX: "auto", maxHeight: 320, lineHeight: 1.6, margin: "10px 0 12px",
    }}>
      {diff.split("\n").map((ln, i) => (
        <span key={i} style={{
          color: ln.startsWith("+") && !ln.startsWith("+++") ? T.green
               : ln.startsWith("-") && !ln.startsWith("---") ? T.red
               : ln.startsWith("@@") ? T.accentL : T.muted,
          display: "block",
        }}>{ln || " "}</span>
      ))}
    </pre>
  );
}

function ApprovalCard({ evt, onApprove, onReject, resolved, resolvedEvt }) {
  const [busy, setBusy] = useState(false);

  if (resolved) {
    const ok = resolved === "approved";
    return (
      <div style={{
        background: ok ? "rgba(52,211,153,0.05)" : "rgba(248,113,113,0.05)",
        border: `1px solid ${ok ? "rgba(52,211,153,0.2)" : "rgba(248,113,113,0.2)"}`,
        borderRadius: 10, padding: "10px 14px", marginBottom: 10,
        fontSize: 12, color: ok ? T.green : T.red,
        display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
      }}>
        {ok ? "✅" : "✕"}
        {ok
          ? `Aprovado e commitado — branch: ${resolvedEvt?.branch_name || "feature/..."}`
          : "Rejeitado — escrita cancelada"}
        {ok && resolvedEvt?.commit_url && (
          <a href={resolvedEvt.commit_url} target="_blank" rel="noopener noreferrer"
            style={{ color: T.accentL, fontSize: 11 }}>
            ver commit →
          </a>
        )}
      </div>
    );
  }

  async function handle(fn, id) {
    setBusy(true);
    await fn(id);
    setBusy(false);
  }

  return (
    <div style={{
      background: "rgba(251,191,36,0.04)", border: `1px solid rgba(251,191,36,0.2)`,
      borderRadius: 12, padding: "14px 16px", marginBottom: 12,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: T.yellow, marginBottom: 10,
        display: "flex", alignItems: "center", gap: 6 }}>
        ✍ Escrever ficheiro — aguarda a tua aprovacao
      </div>
      <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>
        <span style={{ color: T.txt, fontWeight: 600 }}>Ficheiro: </span>
        <code style={{ color: T.accentL, fontSize: 11.5 }}>{evt.file_path}</code>
      </div>
      {evt.commit_message && (
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>
          <span style={{ color: T.txt, fontWeight: 600 }}>Commit: </span>
          {evt.commit_message}
        </div>
      )}
      <DiffView diff={evt.diff} />
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={() => handle(onApprove, evt.approval_id)}
          disabled={busy}
          style={{
            background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.25)",
            borderRadius: 8, padding: "8px 20px", color: T.green,
            fontSize: 12, fontWeight: 700, cursor: busy ? "wait" : "pointer",
            fontFamily: "inherit", opacity: busy ? 0.6 : 1,
          }}
        >{busy ? "A processar..." : "✓ Aprovar e commitar"}</button>
        <button
          onClick={() => handle(onReject, evt.approval_id)}
          disabled={busy}
          style={{
            background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.2)",
            borderRadius: 8, padding: "8px 20px", color: T.red,
            fontSize: 12, fontWeight: 700, cursor: busy ? "wait" : "pointer",
            fontFamily: "inherit", opacity: busy ? 0.6 : 1,
          }}
        >✕ Rejeitar</button>
      </div>
    </div>
  );
}

function ToolCard({ evt }) {
  const icons = { read_file: "📖", write_file: "✍", list_files: "📁", get_recent_commits: "📜" };
  const icon  = icons[evt.tool] || "🔧";
  const sColor = { running: T.muted, done: T.green, error: T.red }[evt.status] || T.muted;
  const sIcon  = { running: "⏳", done: "✓", error: "✕" }[evt.status] || "○";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "3px 0", marginBottom: 3 }}>
      <span style={{ color: sColor, fontSize: 10 }}>{sIcon}</span>
      <span style={{ fontSize: 11 }}>{icon}</span>
      <code style={{ fontSize: 11, color: T.accentL }}>{evt.tool}</code>
      {evt.input?.path && <span style={{ fontSize: 11, color: T.muted }}>{evt.input.path}</span>}
      {evt.status === "error" && evt.output && (
        <span style={{ fontSize: 10, color: T.red }}>{evt.output.slice(0, 60)}</span>
      )}
    </div>
  );
}

function FilesPanel({ files }) {
  if (!files?.length) return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", color: T.muted, gap: 8 }}>
      <span style={{ fontSize: 28 }}>📄</span>
      <p style={{ fontSize: 13 }}>Nenhum ficheiro alterado ainda.</p>
      <p style={{ fontSize: 11 }}>Aparece aqui depois de aprovares escritas do agente.</p>
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.muted,
        textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>
        {files.length} ficheiro(s) alterado(s)
      </div>
      {files.map((f, i) => (
        <div key={i} style={{
          background: T.surf2, border: `1px solid ${T.border}`,
          borderRadius: 10, padding: "12px 14px", marginBottom: 8,
        }}>
          <div style={{ fontSize: 12, color: T.accentL, fontWeight: 600, marginBottom: 4 }}>
            <code>{f.path}</code>
          </div>
          <div style={{ fontSize: 11, color: T.muted }}>Branch: {f.branch}</div>
          {f.commit_message && (
            <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
              {f.commit_message}
            </div>
          )}
          {f.commit_url && (
            <a href={f.commit_url} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 11, color: T.accentL, display: "block", marginTop: 4 }}>
              Ver no GitHub →
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

function MessageBubble({ msg, onApprove, onReject, resolvedApprovals, resolvedEvts }) {
  if (msg.type === "tool_call") return <ToolCard evt={msg.evt} />;

  if (msg.type === "approval") {
    const res  = resolvedApprovals[msg.evt?.approval_id];
    const rEvt = resolvedEvts[msg.evt?.approval_id];
    return (
      <ApprovalCard
        evt={msg.evt}
        onApprove={onApprove}
        onReject={onReject}
        resolved={res}
        resolvedEvt={rEvt}
      />
    );
  }

  if (msg.type === "system_log") {
    const color = msg.level === "error" ? T.red : msg.level === "warning" ? T.yellow : T.muted;
    return (
      <div style={{ fontSize: 11, color, padding: "2px 0", marginBottom: 4,
        display: "flex", alignItems: "center", gap: 5 }}>
        <span>{msg.level === "error" ? "✕" : msg.level === "warning" ? "⚠" : "·"}</span>
        {msg.message}
      </div>
    );
  }

  if (msg.type === "pipeline") {
    const col = { done: T.green, failed: T.red, coding: T.accentL, validating: T.accentL }[msg.stage] || T.muted;
    const ico = { planning: "🧠", coding: "⚡", validating: "🔍", done: "✅", failed: "✕" }[msg.stage] || "○";
    return (
      <div style={{ fontSize: 11, color: col, padding: "2px 0", marginBottom: 6,
        display: "flex", alignItems: "center", gap: 5 }}>
        <span>{ico}</span>
        <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{msg.stage}</span>
        {msg.message && <span style={{ color: T.muted }}>— {msg.message}</span>}
      </div>
    );
  }

  const isUser = msg.role === "user";
  return (
    <div style={{
      display: "flex", flexDirection: isUser ? "row-reverse" : "row",
      gap: 10, marginBottom: 18, alignItems: "flex-start",
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
        background: isUser ? T.accentS : "rgba(52,211,153,0.08)",
        border: `1px solid ${isUser ? T.accentB : "rgba(52,211,153,0.2)"}`,
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12,
      }}>
        {isUser ? "👤" : "⚡"}
      </div>
      <div style={{
        maxWidth: "78%",
        background: isUser ? T.accentS : T.surf2,
        border: `1px solid ${isUser ? T.accentB : T.border}`,
        borderRadius: isUser ? "14px 4px 14px 14px" : "4px 14px 14px 14px",
        padding: "11px 15px", fontSize: 13.5, lineHeight: 1.65, color: T.txt,
      }}>
        {!isUser && (
          <div style={{ fontSize: 10, color: T.muted, marginBottom: 4,
            fontWeight: 600, letterSpacing: "0.05em" }}>
            DEV AGENT
          </div>
        )}
        {renderText(msg.content)}
        {msg.streaming && (
          <span style={{
            display: "inline-block", width: 7, height: 13, background: T.accentL,
            marginLeft: 2, verticalAlign: "middle", borderRadius: 1,
            animation: "ncBlink 1s step-end infinite",
          }} />
        )}
      </div>
    </div>
  );
}

export default function NexusCodeChat({ convId: initConvId, initPrompt, onBack, user }) {
  const [convId,            setConvId]           = useState(initConvId || null);
  const [messages,          setMessages]          = useState([]);
  const [input,             setInput]             = useState("");
  const [isRunning,         setIsRunning]         = useState(false);
  const [pipeStage,         setPipeStage]         = useState("idle");
  const [credits,           setCredits]           = useState(null);
  const [title,             setTitle]             = useState("Nova conversa");
  const [activeTab,         setActiveTab]         = useState("chat");
  const [filesChanged,      setFilesChanged]      = useState([]);
  const [resolvedApprovals, setResolvedApprovals] = useState({}); // id -> "approved"|"rejected"
  const [resolvedEvts,      setResolvedEvts]      = useState({}); // id -> evt data

  const socketRef = useRef(null);
  const endRef    = useRef(null);
  const inputRef  = useRef(null);
  const initDone  = useRef(false);

  useEffect(() => {
    const s = document.createElement("style");
    s.textContent = `
      @keyframes ncBlink { 0%,100%{opacity:1} 50%{opacity:0} }
      @keyframes ncPulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
    `;
    document.head.appendChild(s);
    return () => document.head.removeChild(s);
  }, []);

  const addMsg = useCallback((msg) => {
    setMessages(prev => [...prev, { id: Date.now() + Math.random(), ...msg }]);
  }, []);

  function handleEvent(evt) {
    switch (evt.type) {
      case "pipeline_status":
        setPipeStage(evt.stage);
        addMsg({ type: "pipeline", stage: evt.stage, message: evt.message });
        if (evt.files_changed?.length) setFilesChanged(evt.files_changed);
        if (evt.stage === "done" || evt.stage === "failed") {
          setIsRunning(false);
          setMessages(prev => prev.map(m => m.streaming ? { ...m, streaming: false } : m));
        }
        break;

      case "chunk":
        setMessages(prev => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant" && last.streaming) {
            return [...prev.slice(0, -1), { ...last, content: last.content + (evt.content || "") }];
          }
          return [...prev, { id: Date.now(), role: "assistant", content: evt.content || "", streaming: true, type: "message" }];
        });
        break;

      case "agent_status":
        if (evt.status === "idle") {
          setMessages(prev => prev.map(m => m.streaming ? { ...m, streaming: false } : m));
        }
        break;

      case "tool_call":
        setMessages(prev => {
          const idx = [...prev].reverse().findIndex(
            m => m.type === "tool_call" && m.evt?.tool === evt.tool && m.evt?.status === "running"
          );
          if (idx !== -1) {
            const ri = prev.length - 1 - idx;
            const u = [...prev]; u[ri] = { ...u[ri], evt }; return u;
          }
          return [...prev, { id: Date.now(), type: "tool_call", evt }];
        });
        break;

      case "write_approval_request":
        addMsg({ type: "approval", evt });
        break;

      case "write_approval_resolved":
        setResolvedApprovals(prev => ({ ...prev, [evt.approval_id]: evt.decision }));
        setResolvedEvts(prev => ({ ...prev, [evt.approval_id]: evt }));
        break;

      case "execution_complete":
        setIsRunning(false);
        setPipeStage("done");
        setMessages(prev => prev.map(m => m.streaming ? { ...m, streaming: false } : m));
        ncBilling.credits().then(r => { if (r.data) setCredits(r.data); });
        break;

      case "log":
        if (evt.log_type === "error" || evt.log_type === "warning")
          addMsg({ type: "system_log", level: evt.log_type, message: evt.message });
        break;

      case "error":
        setIsRunning(false);
        setPipeStage("failed");
        setMessages(prev => prev.map(m => m.streaming ? { ...m, streaming: false } : m));
        addMsg({ type: "system_log", level: "error", message: evt.message || "Erro desconhecido" });
        break;

      default: break;
    }
  }

  function openSocket(cId) {
    socketRef.current?.close();
    socketRef.current = createNcSocket(cId, { onEvent: handleEvent });
  }

  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;

    ncBilling.credits().then(r => { if (r.data) setCredits(r.data); });

    if (initConvId) {
      openSocket(initConvId);
      ncConversations.getMessages(initConvId).then(r => {
        if (r.data?.messages) {
          setMessages(r.data.messages.map(m => ({
            id: m._id || Math.random(),
            role: m.role, content: m.content, type: "message",
          })));
        }
      });
    } else {
      const t = initPrompt?.slice(0, 60) || "Nova conversa";
      ncConversations.create(t).then(r => {
        if (!r.data) return;
        const cId = r.data.conv_id;
        setConvId(cId);
        setTitle(r.data.title || t);
        openSocket(cId);
        if (initPrompt) doSend(initPrompt, cId);
      });
    }
    return () => socketRef.current?.close();
  }, []); // eslint-disable-line

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function doSend(text, cId) {
    const tgt = cId || convId;
    if (!text?.trim() || !tgt) return;
    setIsRunning(true);
    setPipeStage("planning");
    addMsg({ role: "user", content: text, type: "message" });
    const { data, error } = await ncExecute.run(text, tgt);
    if (error) {
      setIsRunning(false);
      setPipeStage("failed");
      addMsg({ type: "system_log", level: "error", message: error });
    } else if (data?.balance_after != null) {
      setCredits(prev => prev ? { ...prev, balance: data.balance_after } : prev);
    }
  }

  function send() {
    const t = input.trim();
    if (!t || isRunning) return;
    setInput("");
    doSend(t);
  }

  function stop() {
    socketRef.current?.send({ type: "stop" });
    setIsRunning(false);
    setPipeStage("idle");
    setMessages(prev => prev.map(m => m.streaming ? { ...m, streaming: false } : m));
  }

  async function onApprove(id) {
    await ncApprovals.approve(id);
    setResolvedApprovals(prev => ({ ...prev, [id]: "approved" }));
  }
  async function onReject(id) {
    await ncApprovals.reject(id);
    setResolvedApprovals(prev => ({ ...prev, [id]: "rejected" }));
  }

  const tabBtn = (key, label) => (
    <button key={key} onClick={() => setActiveTab(key)} style={{
      background: activeTab === key ? T.accentS : "transparent",
      border: `1px solid ${activeTab === key ? T.accentB : "transparent"}`,
      borderRadius: 8, padding: "5px 14px", fontSize: 12, fontWeight: 600,
      color: activeTab === key ? T.accentL : T.muted,
      cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
      position: "relative",
    }}>
      {label}
      {key === "code" && filesChanged.length > 0 && (
        <span style={{
          position: "absolute", top: -4, right: -4,
          background: T.green, color: "#000", borderRadius: "50%",
          fontSize: 8, fontWeight: 800, width: 14, height: 14,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>{filesChanged.length}</span>
      )}
    </button>
  );

  return (
    <div style={{
      display: "flex", flexDirection: "column", height: "100vh",
      background: T.bg, color: T.txt, fontFamily: "'Manrope', system-ui, sans-serif",
    }}>
      {/* Header */}
      <header style={{
        height: 52, display: "flex", alignItems: "center",
        justifyContent: "space-between", padding: "0 20px",
        borderBottom: `1px solid ${T.border}`, background: T.surface, flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={onBack} style={{
            background: "transparent", border: `1px solid ${T.border}`,
            borderRadius: 7, padding: "4px 10px", color: T.muted,
            fontSize: 12, cursor: "pointer", fontFamily: "inherit",
          }}>← Home</button>
          <span style={{ fontSize: 13, fontWeight: 600, color: T.txt,
            maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {title}
          </span>
          {isRunning && (
            <span style={{
              fontSize: 10, color: T.accentL, background: T.accentS,
              border: `1px solid ${T.accentB}`, borderRadius: 10,
              padding: "2px 10px", whiteSpace: "nowrap",
              animation: "ncPulse 2s ease-in-out infinite",
            }}>
              ● Agente a trabalhar
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {credits && (
            <span style={{
              fontSize: 11, marginRight: 4,
              color: (!credits.unlimited && credits.balance < 1) ? T.red : T.muted,
              background: T.surf2, border: `1px solid ${T.border}`,
              borderRadius: 20, padding: "3px 10px",
            }}>
              {credits.unlimited ? "∞ admin" : `${credits.balance ?? 0} cr`}
            </span>
          )}
          {tabBtn("chat", "💬 Chat")}
          {tabBtn("code", "📄 Code")}
          {tabBtn("deploy", "🚀 Deploy")}
        </div>
      </header>

      {/* Pipeline bar */}
      <PipelineBar stage={pipeStage} />

      {/* Content */}
      {activeTab === "chat" && (
        <>
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 28px" }}>
            {messages.length === 0 && (
              <div style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                justifyContent: "center", minHeight: 260,
                color: T.muted, fontSize: 13, gap: 8,
              }}>
                <span style={{ fontSize: 28 }}>⚡</span>
                Pronto para construir. Envia o teu pedido abaixo.
              </div>
            )}
            {messages.map(msg => (
              <MessageBubble
                key={msg.id}
                msg={msg}
                onApprove={onApprove}
                onReject={onReject}
                resolvedApprovals={resolvedApprovals}
                resolvedEvts={resolvedEvts}
              />
            ))}
            <div ref={endRef} />
          </div>

          <div style={{
            padding: "10px 20px 14px",
            borderTop: `1px solid ${T.border}`, background: T.surface,
          }}>
            <div style={{
              display: "flex", alignItems: "flex-end", gap: 10,
              background: "#040509",
              border: `1px solid ${isRunning ? T.accentB : T.border}`,
              borderRadius: 14, padding: "10px 14px", transition: "border-color 0.2s",
            }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder={isRunning ? "Agente a trabalhar..." : "Proximo passo? (Shift+Enter para nova linha)"}
                disabled={isRunning}
                rows={2}
                style={{
                  flex: 1, background: "transparent", border: "none", outline: "none",
                  color: isRunning ? T.muted : T.txt, fontSize: 14, lineHeight: 1.6,
                  resize: "none", fontFamily: "inherit",
                }}
              />
              {isRunning ? (
                <button onClick={stop} style={{
                  background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.2)",
                  borderRadius: 8, padding: "6px 14px", color: T.red,
                  fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                }}>⬛ Stop</button>
              ) : (
                <button onClick={send} disabled={!input.trim()} style={{
                  background: input.trim() ? T.accent : "rgba(124,58,237,0.2)",
                  border: "none", borderRadius: 8, padding: "6px 18px",
                  color: input.trim() ? "#fff" : T.muted,
                  fontSize: 13, fontWeight: 700,
                  cursor: input.trim() ? "pointer" : "not-allowed",
                  fontFamily: "inherit", transition: "background 0.15s",
                }}>Enviar ↵</button>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === "code" && <FilesPanel files={filesChanged} />}

      {activeTab === "deploy" && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 10, color: T.muted }}>
          <span style={{ fontSize: 28 }}>🚀</span>
          {filesChanged.length === 0 ? (
            <p style={{ fontSize: 13 }}>Ainda sem ficheiros commitados. Aprova escritas do agente primeiro.</p>
          ) : (
            <>
              <p style={{ fontSize: 13 }}>{filesChanged.length} ficheiro(s) commitados em branches isoladas.</p>
              <p style={{ fontSize: 11 }}>Faz merge no GitHub e deploya no Render, Vercel ou Railway.</p>
              <a href="https://github.com" target="_blank" rel="noopener noreferrer" style={{
                marginTop: 8, background: T.accent, color: "#fff",
                borderRadius: 8, padding: "9px 22px", fontSize: 13,
                fontWeight: 700, textDecoration: "none",
              }}>Abrir GitHub →</a>
            </>
          )}
        </div>
      )}
    </div>
  );
}
